import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Webhook as WebhookIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as TestIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Timeline as StatsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import { externalApiService } from '../services/externalApiService';
import WebhookDashboard from './WebhookDashboard';

const WEBHOOK_EVENTS = [
  { value: 'conversation.completed', label: 'Conversa Concluída', description: 'Quando uma conversa via API é finalizada' },
  { value: 'rate_limit.warning', label: 'Alerta Rate Limit', description: 'Quando uso atinge 80% do limite' },
  { value: 'rate_limit.exceeded', label: 'Rate Limit Excedido', description: 'Quando limite é ultrapassado' },
  { value: 'billing.threshold.warning', label: 'Alerta de Gastos', description: 'Quando gastos atingem threshold' },
  { value: 'api_key.created', label: 'API Key Criada', description: 'Quando nova API key é gerada' },
  { value: 'api_key.expired', label: 'API Key Expirada', description: 'Quando API key expira' },
  { value: 'system.alert', label: 'Alerta do Sistema', description: 'Alertas gerais do sistema' },
];

const WebhookManager = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testDialog, setTestDialog] = useState({ open: false, webhook: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, webhook: null });
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [dashboardDialog, setDashboardDialog] = useState({ open: false, webhook: null });
  
  // 🔄 NOVO: Estado para API keys
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    url: '',
    description: '',
    subscribed_events: [],
    max_retries: 3,
    timeout_seconds: 30,
    agent_filter: [],
    channel_filter: [],
    api_key_id: ''  // 🔄 NOVO: API key específica (vazio = webhook global)
  });

  // Load webhooks
  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await externalApiService.getWebhooks();
      setWebhooks(response || []);
      
    } catch (err) {
      console.error('Erro ao carregar webhooks:', err);
      setError('Erro ao carregar webhooks. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // 🔄 NOVO: Carregar API keys para dropdown
  const loadApiKeys = async () => {
    try {
      console.log('🔄 WebhookManager: Iniciando carregamento de API keys...');
      setApiKeysLoading(true);
      const keys = await externalApiService.getApiKeysForDropdown();
      console.log('🔄 WebhookManager: API keys recebidas:', keys);
      setApiKeys(keys);
      console.log('🔄 WebhookManager: Estado atualizado com', keys.length, 'API keys');
    } catch (err) {
      console.error('❌ WebhookManager: Erro ao carregar API keys:', err);
      // Não mostrar erro crítico, pois é opcional
    } finally {
      setApiKeysLoading(false);
      console.log('🔄 WebhookManager: Loading finalizado');
    }
  };

  useEffect(() => {
    loadWebhooks();
  }, []);

  // 🔄 NOVO: Carregar API keys quando abrir diálogo
  useEffect(() => {
    console.log('🔄 WebhookManager: Dialog state changed:', dialogOpen);
    if (dialogOpen) {
      console.log('🔄 WebhookManager: Dialog aberto, carregando API keys...');
      loadApiKeys();
    }
  }, [dialogOpen]);

  // Auto-clear messages
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (editingWebhook) {
        await externalApiService.updateWebhook(editingWebhook.id, formData);
        setSuccessMessage('Webhook atualizado com sucesso!');
      } else {
        await externalApiService.createWebhook(formData);
        setSuccessMessage('Webhook criado com sucesso!');
      }
      
      setDialogOpen(false);
      setEditingWebhook(null);
      setFormData({
        url: '',
        description: '',
        subscribed_events: [],
        max_retries: 3,
        timeout_seconds: 30,
        agent_filter: [],
        channel_filter: [],
        api_key_id: ''
      });
      loadWebhooks();
      
    } catch (err) {
      console.error('Erro ao salvar webhook:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar webhook');
    }
  };

  // Handle test webhook
  const handleTestWebhook = async (webhook) => {
    try {
      await externalApiService.testWebhook(webhook.id);
      setSuccessMessage('Webhook de teste enviado com sucesso!');
      setTestDialog({ open: false, webhook: null });
    } catch (err) {
      console.error('Erro ao testar webhook:', err);
      setError(err.response?.data?.detail || 'Erro ao testar webhook');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await externalApiService.deleteWebhook(deleteDialog.webhook.id);
      setSuccessMessage('Webhook deletado com sucesso!');
      setDeleteDialog({ open: false, webhook: null });
      loadWebhooks();
    } catch (err) {
      console.error('Erro ao deletar webhook:', err);
      setError(err.response?.data?.detail || 'Erro ao deletar webhook');
    }
  };

  // Open edit dialog
  // 🔄 NOVO: Função para criar novo webhook (limpar formulário)
  const handleCreateNew = () => {
    setEditingWebhook(null);  // Limpar webhook sendo editado
    setFormData({
      url: '',
      description: '',
      subscribed_events: [],
      max_retries: 3,
      timeout_seconds: 30,
      agent_filter: [],
      channel_filter: [],
      api_key_id: ''  // Webhook global por padrão
    });
    setDialogOpen(true);
  };

  const handleEdit = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      url: webhook.url,
      description: webhook.description || '',
      subscribed_events: webhook.subscribed_events || [],
      max_retries: webhook.max_retries || 3,
      timeout_seconds: webhook.timeout_seconds || 30,
      agent_filter: webhook.agent_filter || [],
      channel_filter: webhook.channel_filter || [],
      api_key_id: webhook.api_key_id || ''  // 🔄 NOVO: API key do webhook
    });
    setDialogOpen(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Get status color
  const getStatusColor = (webhook) => {
    if (!webhook.is_active) return 'default';
    if (webhook.failure_count > 10) return 'error';
    if (webhook.failure_count > 5) return 'warning';
    return 'success';
  };

  // Get status text
  const getStatusText = (webhook) => {
    if (!webhook.is_active) return 'Inativo';
    if (webhook.failure_count > 10) return 'Com Problemas';
    if (webhook.failure_count > 5) return 'Instável';
    return 'Funcionando';
  };

  // Handle view stats
  const handleViewStats = (webhook) => {
    setDashboardDialog({ open: true, webhook });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="body2" color="text.secondary">
          Configure endpoints para receber notificações em tempo real
        </Typography>
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Novo Webhook
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WebhookIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum webhook configurado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Configure seu primeiro webhook para receber notificações automáticas
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Criar Primeiro Webhook
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {webhooks.map((webhook) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={webhook.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card sx={{
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  },
                  transition: 'all 0.3s ease',
                }}>
                  <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header compacto */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <WebhookIcon 
                          color={webhook.is_active ? "primary" : "error"}
                          sx={{ fontSize: 16 }}
                        />
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                        >
                          {new URL(webhook.url).hostname}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={getStatusText(webhook)}
                          color={getStatusColor(webhook)}
                          size="small"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 18,
                            fontWeight: 500,
                          }}
                        />
                        <Chip
                          label={`${webhook.subscribed_events.length} eventos`}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 18,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Descrição compacta */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      {webhook.description || 'Sem descrição'}
                    </Typography>

                    {/* 🔄 NOVO: Exibir API Key associada */}
                    <Box sx={{ mb: 1 }}>
                      {webhook.api_key_id ? (
                        <Chip
                          label={`🔑 API Key específica`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 20,
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <Chip
                          label={`🌐 Webhook Global`}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 20,
                            fontWeight: 500,
                          }}
                        />
                      )}
                    </Box>

                    {/* Stats compactas */}
                    <Box sx={{ mt: 'auto', pt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          ✅ {webhook.delivery_count} | ❌ {webhook.failure_count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(webhook.last_delivery_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Testar">
                        <IconButton
                          size="small"
                          onClick={() => setTestDialog({ open: true, webhook })}
                          sx={{ p: 0.5 }}
                        >
                          <TestIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(webhook)}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deletar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, webhook })}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver estatísticas">
                        <IconButton
                          size="small"
                          onClick={() => handleViewStats(webhook)}
                          sx={{ p: 0.5 }}
                        >
                          <StatsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setEditingWebhook(null);  // Limpar estado de edição
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingWebhook ? 'Editar Webhook' : 'Criar Novo Webhook'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="URL do Webhook"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://api.seusite.com/webhooks/organia"
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />

            {/* 🔄 NOVO: Seleção de API Key */}
            <FormControl fullWidth margin="normal">
              <InputLabel>API Key (Opcional)</InputLabel>
              <Select
                value={formData.api_key_id}
                onChange={(e) => setFormData({ ...formData, api_key_id: e.target.value })}
                label="API Key (Opcional)"
                disabled={apiKeysLoading}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      🌐 Webhook Global
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Todas as API keys da empresa)
                    </Typography>
                  </Box>
                </MenuItem>
                {apiKeys.length > 0 ? (
                  apiKeys.map((key) => (
                    <MenuItem key={key.id} value={key.id}>
                      <Box>
                        <Typography variant="body2">
                          🔑 {key.key_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {key.key_preview}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  !apiKeysLoading && (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        ⚠️ Nenhuma API key encontrada
                      </Typography>
                    </MenuItem>
                  )
                )}
              </Select>
              {apiKeysLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Carregando API keys...
                  </Typography>
                </Box>
              )}
              {apiKeys.length === 0 && !apiKeysLoading ? (
                <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="caption">
                    ℹ️ Nenhuma API key encontrada. O webhook será criado como <strong>Global</strong> (recebe eventos de todas as API keys).
                  </Typography>
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💡 <strong>Webhook Global:</strong> Recebe eventos de todas as API keys da empresa<br/>
                  🔑 <strong>Webhook Específico:</strong> Recebe apenas eventos da API key selecionada
                </Typography>
              )}
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Eventos para Receber:
            </Typography>
            <FormGroup>
              {WEBHOOK_EVENTS.map((event) => (
                <FormControlLabel
                  key={event.value}
                  control={
                    <Checkbox
                      checked={formData.subscribed_events.includes(event.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            subscribed_events: [...formData.subscribed_events, event.value]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            subscribed_events: formData.subscribed_events.filter(ev => ev !== event.value)
                          });
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{event.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Tentativas"
                  value={formData.max_retries}
                  onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value) })}
                  inputProps={{ min: 0, max: 10 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout (segundos)"
                  value={formData.timeout_seconds}
                  onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                  inputProps={{ min: 5, max: 120 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setEditingWebhook(null);  // Limpar estado de edição
          }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.url || formData.subscribed_events.length === 0}
          >
            {editingWebhook ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialog.open} onClose={() => setTestDialog({ open: false, webhook: null })}>
        <DialogTitle>Testar Webhook</DialogTitle>
        <DialogContent>
          <Typography>
            Enviar um webhook de teste para <strong>{testDialog.webhook?.url}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Um evento do tipo "system.alert" será enviado para validar seu endpoint.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog({ open: false, webhook: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => handleTestWebhook(testDialog.webhook)}
          >
            Enviar Teste
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, webhook: null })}>
        <DialogTitle>Deletar Webhook</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar o webhook <strong>{deleteDialog.webhook?.url}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, webhook: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dashboard de Estatísticas */}
      <WebhookDashboard
        open={dashboardDialog.open}
        onClose={() => setDashboardDialog({ open: false, webhook: null })}
        webhook={dashboardDialog.webhook}
      />
    </Box>
  );
};

export default WebhookManager;
